import { exactCurrencyFormatter } from "../../../utils"
import { format_period_header, parse_period_key_to_time_period } from "../utils/periods"
import type { UnsavedCellChange, UnsavedDiff, UnsavedField, UnsavedRowChange } from "../utils/unsaved_diff"

interface UnsavedChangesListProps {
  diff: UnsavedDiff
  className?: string
}

const FIELD_LABEL: Record<UnsavedField, string> = {
  store_count: "Doors",
  cases_sold: "Cases",
  units_sold: "Units",
  volume_dollars: "Revenue",
}

function format_value(field: UnsavedField, value: number): string {
  if (field === "volume_dollars") return exactCurrencyFormatter(value)
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 4 })
}

function period_label(period: string): string {
  const parsed = parse_period_key_to_time_period(period)
  return parsed ? format_period_header(parsed) : period
}

/** The unsaved edits, row by row and cell by cell, as `diff_unsaved_forecast` found them. */
export function UnsavedChangesList({ diff, className }: UnsavedChangesListProps) {
  const total = diff.rows.length + diff.cell_count
  if (total === 0) {
    return <p className={`text-xs text-gray-500 ${className ?? ""}`}>Nothing differs from the saved version.</p>
  }
  return (
    <div className={`max-h-72 overflow-y-auto rounded-md border border-gray-200 ${className ?? ""}`}>
      <ul className="divide-y divide-gray-100 text-xs">
        {diff.rows.map((row: UnsavedRowChange) => (
          <li key={`${row.kind}:${row.path}`} className="flex items-start gap-2 px-3 py-1.5">
            <span className={`shrink-0 font-medium ${row.kind === "added" ? "text-emerald-700" : "text-red-700"}`}>
              {row.kind === "added" ? "Added" : "Removed"}
            </span>
            <span className="text-gray-800">{row.path}</span>
          </li>
        ))}
        {diff.cells.map((cell: UnsavedCellChange) => (
          <li key={`${cell.path}|${cell.period}|${cell.field}`} className="px-3 py-1.5">
            <div className="truncate text-gray-800" title={cell.path}>
              {cell.path}
            </div>
            <div className="text-gray-600">
              {period_label(cell.period)} · {FIELD_LABEL[cell.field]}:{" "}
              <span className="line-through">{format_value(cell.field, cell.before)}</span> →{" "}
              <span className="font-medium text-gray-900">{format_value(cell.field, cell.after)}</span>
            </div>
          </li>
        ))}
      </ul>
      {diff.cell_count > diff.cells.length ? (
        <p className="border-t border-gray-100 px-3 py-1.5 text-[11px] text-gray-500">
          …and {(diff.cell_count - diff.cells.length).toLocaleString()} more changed cells.
        </p>
      ) : null}
    </div>
  )
}
